import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { b2bFilterContacts, b2bCreateContacts, b2bDeleteContact, b2bGetAccounts, b2bGetContactOptions } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

export default function B2BContacts() {
  const navigate = useNavigate();
  const [contacts, setContacts]   = useState([]);
  const [total, setTotal]         = useState(0);
  const [showing, setShowing]     = useState(0);
  const [accounts, setAccounts]   = useState([]);
  const [options, setOptions]     = useState({ standard_titles: [], industries: [], size_buckets: [] });
  const [loading, setLoading]     = useState(true);
  const [showNew, setShowNew]     = useState(false);
  const [saving, setSaving]       = useState(false);

  // filters
  const [search,     setSearch]     = useState('');
  const [stdTitle,   setStdTitle]   = useState('');
  const [industry,   setIndustry]   = useState('');
  const [sizeBucket, setSizeBucket] = useState('');
  const [limit,      setLimit]      = useState(200);

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', title: '', phone: '', account_id: '', linkedin_url: '', apollo_id: '' });

  const load = useCallback(() => {
    setLoading(true);
    b2bFilterContacts({ search, standard_titles: stdTitle, industries: industry, size_buckets: sizeBucket, limit })
      .then(r => {
        setContacts(r.contacts || []);
        setTotal(r.total ?? 0);
        setShowing(r.showing ?? (r.contacts || []).length);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [search, stdTitle, industry, sizeBucket, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { b2bGetAccounts().then(r => setAccounts(r.accounts || [])).catch(() => {}); }, []);
  useEffect(() => { b2bGetContactOptions().then(r => setOptions(r)).catch(() => {}); }, []);

  function clearFilters() {
    setSearch(''); setStdTitle(''); setIndustry(''); setSizeBucket('');
  }

  const hasFilters = search || stdTitle || industry || sizeBucket;

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await b2bCreateContacts({ ...form, account_id: form.account_id ? parseInt(form.account_id) : null });
      setShowNew(false);
      setForm({ first_name: '', last_name: '', email: '', title: '', phone: '', account_id: '', linkedin_url: '', apollo_id: '' });
      load();
    } catch (err) { alert(err.message); }
    setSaving(false);
  }

  const sel = { ...s.inp, padding: '7px 10px', fontSize: 12 };

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-contacts" />
      <main style={s.main}>
        <div style={s.topRow}>
          <h1 style={s.title}>Contacts</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={s.importBtn} onClick={() => navigate('/admin/b2b/import/apollo')}>↑ Import from Apollo</button>
            <button style={s.primaryBtn} onClick={() => setShowNew(true)}>+ New Contact</button>
          </div>
        </div>

        {/* Filter bar */}
        <div style={s.filterBar}>
          <input
            style={{ ...s.inp, width: 200, padding: '7px 10px', fontSize: 12 }}
            placeholder="Search name / email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select style={sel} value={stdTitle} onChange={e => setStdTitle(e.target.value)}>
            <option value="">All Titles</option>
            {options.standard_titles.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select style={sel} value={industry} onChange={e => setIndustry(e.target.value)}>
            <option value="">All Industries</option>
            {options.industries.map(i => <option key={i} value={i}>{i}</option>)}
          </select>
          <select style={sel} value={sizeBucket} onChange={e => setSizeBucket(e.target.value)}>
            <option value="">All Sizes</option>
            {options.size_buckets.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <select style={{ ...sel, width: 120 }} value={limit} onChange={e => setLimit(parseInt(e.target.value))}>
            <option value={200}>Top 200</option>
            <option value={500}>Top 500</option>
            <option value={1000}>Top 1,000</option>
            <option value={2000}>Top 2,000</option>
          </select>
          {hasFilters && (
            <button style={s.clearBtn} onClick={clearFilters}>✕ Clear</button>
          )}
        </div>

        {/* Count row */}
        <div style={s.countRow}>
          {loading ? 'Loading…' : (
            <span>
              Showing <strong>{showing.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong> contacts
              {hasFilters && <span style={{ color: '#94a3b8' }}> (filtered)</span>}
            </span>
          )}
        </div>

        {showNew && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>New Contact</h2>
              <form onSubmit={handleCreate} style={{ display: 'grid', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input style={s.inp} placeholder="First Name" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
                  <input style={s.inp} placeholder="Last Name" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
                </div>
                <input required style={s.inp} placeholder="Email *" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
                <input style={s.inp} placeholder="Title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                <input style={s.inp} placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <select style={s.inp} value={form.account_id} onChange={e => setForm(f => ({ ...f, account_id: e.target.value }))}>
                  <option value="">No Account</option>
                  {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.company_name}</option>)}
                </select>
                <input style={s.inp} placeholder="LinkedIn URL" value={form.linkedin_url} onChange={e => setForm(f => ({ ...f, linkedin_url: e.target.value }))} />
                <input style={s.inp} placeholder="Apollo ID (for dedup)" value={form.apollo_id} onChange={e => setForm(f => ({ ...f, apollo_id: e.target.value }))} />
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowNew(false)}>Cancel</button>
                  <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Name</th>
                <th style={s.th}>Company</th>
                <th style={s.th}>Standard Title</th>
                <th style={s.th}>Title</th>
                <th style={s.th}>Industry</th>
                <th style={s.th}>Size</th>
                <th style={s.th}>Email</th>
                <th style={s.th}>Added</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={s.empty}>Loading…</td></tr>}
              {!loading && contacts.length === 0 && <tr><td colSpan={9} style={s.empty}>No contacts found</td></tr>}
              {contacts.map(c => (
                <tr key={c.contact_id} style={s.tr}>
                  <td style={{ ...s.td, fontWeight: 500, color: NAVY, whiteSpace: 'nowrap' }}>
                    <Link to={`/admin/b2b/contacts/${c.contact_id}`} style={{ color: NAVY, textDecoration: 'none' }}>
                      {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                    </Link>
                  </td>
                  <td style={s.td}>
                    {c.company_name
                      ? <Link to={`/admin/b2b/accounts/${c.account_id}`} style={{ color: BLUE, textDecoration: 'none', fontSize: 12 }}>{c.company_name}</Link>
                      : '—'}
                  </td>
                  <td style={s.td}>
                    {c.standard_title
                      ? <span style={s.badge}>{c.standard_title}</span>
                      : <span style={{ color: '#94a3b8', fontSize: 11 }}>—</span>}
                  </td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={c.title}>{c.title || '—'}</td>
                  <td style={{ ...s.td, fontSize: 12 }}>{c.standard_industry || c.industry || '—'}</td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>{c.size_bucket || '—'}</td>
                  <td style={{ ...s.td, color: BLUE, fontSize: 12 }}>{c.email || '—'}</td>
                  <td style={{ ...s.td, color: '#94a3b8', fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(c.created_on_utc).toLocaleDateString()}</td>
                  <td style={s.td}>
                    <button style={s.deleteBtn} onClick={async () => { if (confirm('Delete contact?')) { await b2bDeleteContact(c.contact_id); load(); } }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const s = {
  page:      { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main:      { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  topRow:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  title:     { color: NAVY, fontSize: 24, fontWeight: 700, margin: 0 },
  filterBar: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10, alignItems: 'center' },
  countRow:  { fontSize: 12, color: '#64748b', marginBottom: 12 },
  primaryBtn:{ background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  importBtn: { background: '#fff', color: BLUE, border: `1px solid ${BLUE}`, borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  clearBtn:  { background: 'transparent', color: '#ef4444', border: '1px solid #fca5a5', borderRadius: 8, padding: '6px 12px', fontSize: 12, cursor: 'pointer' },
  card:      { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table:     { width: '100%', borderCollapse: 'collapse' },
  th:        { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' },
  tr:        { borderBottom: '1px solid #f1f5f9' },
  td:        { color: '#374151', fontSize: 13, padding: '9px 12px', verticalAlign: 'middle' },
  empty:     { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  badge:     { background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' },
  deleteBtn: { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 },
  modal:     { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox:  { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  inp:       { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
};
